import {
  AcademicCapIcon,
  BriefcaseIcon,
  CheckBadgeIcon,
  PresentationChartLineIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { TabProps } from '../personFormSchema';

// ─── Tipos ────────────────────────────────────────────────────────────────────

type RoleFieldName = 'isStudent' | 'isMonitor' | 'isEmployee' | 'isTeachingStaff';

interface RoleDescriptor {
  readonly name: RoleFieldName;
  readonly title: string;
  readonly description: string;
  readonly icon: ComponentType<SVGProps<SVGSVGElement>>;
}

// ─── Card individual ──────────────────────────────────────────────────────────

interface RoleCardProps {
  readonly role: RoleDescriptor;
  readonly checked: boolean;
  readonly onToggle: (next: boolean) => void;
}

function RoleCard({ role, checked, onToggle }: RoleCardProps) {
  const IconComponent = role.icon;

  const borderClass = checked
    ? 'border-border-ctx-highlight-ringFocusShadow'
    : 'border-ctx-layout-border';
  const bgClass = checked ? 'bg-ctx-layout-spotlight' : 'bg-ctx-layout-body';
  const iconBg = checked
    ? 'bg-base-success-200 text-base-success-500'
    : 'bg-ctx-layout-spotlight text-ctx-content-title';

  return (
    <button
      type="button"
      aria-pressed={checked}
      onClick={() => onToggle(!checked)}
      className={`group relative flex flex-col bg-ctx-content-primary items-start gap-xxs rounded-md border-thin ${borderClass} ${bgClass} p-xs text-left transition-colors`}
    >
      <div className="flex w-full items-start justify-between gap-xxs">
        <span
          className={`flex h-sm w-sm items-center justify-center rounded-sm ${iconBg}`}
          aria-hidden="true"
        >
          <IconComponent className="h-xxs w-xxs" />
        </span>
        {checked && (
          <span
            className="flex h-xxxs w-xxxs items-center justify-center rounded-pill text-ctx-content-title"
            aria-hidden="true"
          >
            <CheckBadgeIcon className="size-xxs" />
          </span>
        )}
      </div>
      <div className="flex flex-col gap-quark">
        <span className="ts-heading-xxs text-ctx-content-title">{role.title}</span>
        <span className="ts-caption-xxs text-ctx-content-base">{role.description}</span>
      </div>
    </button>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

/**
 * Grade de cartões selecionáveis para definir os perfis de acesso de uma pessoa.
 * Cada cartão representa um papel (aluno, monitor, funcionário, docente) com
 * ícone, título e descrição. Substitui a grade de checkboxes plana.
 */
export function ProfileRoleCards({ control }: Pick<TabProps, 'control'>) {
  const { t } = useTranslation('personManagement');

  const roles: readonly RoleDescriptor[] = [
    {
      name: 'isStudent',
      title: t('personForm.fields.isStudent'),
      description: t('personForm.roles.isStudentDescription'),
      icon: AcademicCapIcon,
    },
    {
      name: 'isMonitor',
      title: t('personForm.fields.isMonitor'),
      description: t('personForm.roles.isMonitorDescription'),
      icon: UserGroupIcon,
    },
    {
      name: 'isEmployee',
      title: t('personForm.fields.isEmployee'),
      description: t('personForm.roles.isEmployeeDescription'),
      icon: BriefcaseIcon,
    },
    {
      name: 'isTeachingStaff',
      title: t('personForm.fields.isTeacher'),
      description: t('personForm.roles.isTeacherDescription'),
      icon: PresentationChartLineIcon,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-xs @md:grid-cols-2 @3xl:grid-cols-4">
      {roles.map(role => (
        <Controller
          key={role.name}
          name={role.name}
          control={control}
          render={({ field }) => (
            <RoleCard role={role} checked={field.value === true} onToggle={field.onChange} />
          )}
        />
      ))}
    </div>
  );
}
